import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import { can } from '../auth/AuthContext'
import { useCompanies } from '../companies/useCompanies'
import { formatInt, initials } from '../lib/format'
import { BbIcon } from './BbIcon'
import { Dropdown } from './Dropdown'
import { useOpenSettings } from '../pages/settings/useOpenSettings'

/* The BatchBooks top app bar, shared by every /app route.

   Follows "prototypes/BatchBooks Import - Select Type", which supersedes the
   earlier dashboard prototype: it adds the wordmark, drops the "ACTIVE COMPANY"
   chip in favour of a plain company name, moves credits into a pill and makes
   the avatar a green initials badge. The dashboard bar's help button is gone
   with it — the FAQ is still linked from the footer.

   The prototypes open the menus on :hover, which is unusable on touch and
   unreachable by keyboard, so the shared Dropdown component is used instead
   (click to open, closes on outside click or Escape). */

function CreditsPill() {
  const { user } = useAuth()
  const credits = user?.credits
  if (!credits) return null

  const granted = credits.included + credits.purchased
  const low = credits.available <= granted * 0.1

  return (
    <span
      className={low ? 'bb-credits bb-credits-low' : 'bb-credits'}
      title={`${formatInt(credits.used)} of ${formatInt(granted)} credits used`}
    >
      <BbIcon name="coins" />
      Credits: {formatInt(credits.available)}
    </span>
  )
}

function CompanySwitcher() {
  const { companies, connected, current, select } = useCompanies()
  const navigate = useNavigate()

  const goCompanies = (close: () => void) => {
    close()
    navigate('/app/companies')
  }

  return (
    <Dropdown
      align="left"
      trigger={
        <span className="bb-co">
          <span className="bb-co-name">{current?.companyName ?? 'No company connected'}</span>
          <BbIcon name="chevron-down" />
        </span>
      }
    >
      {(close) => (
        <>
          {/* Only live companies are offered here. This menu switches the active
              context, and a disconnected company cannot run anything, so listing
              one would present it as usable when it is not. The full list,
              disconnected rows included, is the Switch Company screen's job. */}
          <div className="dd-h">Connected companies · {connected.length}</div>
          {connected.length === 0 ? (
            <div className="dd-i" style={{ color: 'var(--bb-on-surface-variant)' }}>
              {companies.length > 0 ? 'No connected companies' : 'Nothing connected yet'}
            </div>
          ) : (
            connected.map((company) => (
              <button
                key={company.id}
                type="button"
                className={company.id === current?.id ? 'dd-i on' : 'dd-i'}
                onClick={() => {
                  select(company.id)
                  close()
                }}
              >
                {company.companyName} <span className="sub">{company.country ?? company.status}</span>
              </button>
            ))
          )}
          <div className="dd-sep" />
          <button className="dd-i" type="button" onClick={() => goCompanies(close)}>
            <BbIcon name="building" />
            Switch company
          </button>
        </>
      )}
    </Dropdown>
  )
}

function UserMenu() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  // Before the early return: a hook called only when `user` is set would change
  // the hook order between renders.
  const openSettings = useOpenSettings()
  if (!user) return null

  const go = (path: string, close: () => void) => {
    close()
    navigate(path)
  }

  return (
    <Dropdown
      buttonClassName="dd-btn bb-user-btn"
      trigger={
        <span className="bb-user">
          <span className="bb-user-name">{user.name || user.email}</span>
          <span className="bb-avatar-sm">{initials(user.name, user.email)}</span>
        </span>
      }
    >
      {(close) => (
        <>
          <div className="bb-menu-head">
            <span className="bb-menu-avatar">{initials(user.name, user.email)}</span>
            <span className="bb-menu-who">
              <span className="bb-menu-name">{user.name || user.email}</span>
              <span className="bb-menu-mail">{user.email}</span>
            </span>
          </div>
          <div className="bb-menu-meta">
            {user.tenantName} · {user.planName} plan · {user.role}
          </div>

          <div className="bb-menu-list">
            <button className="dd-i" type="button" onClick={() => go('/app/account', close)}>
              <BbIcon name="users" />
              Account
            </button>
            <button className="dd-i" type="button" onClick={() => go('/app/companies', close)}>
              <BbIcon name="building" />
              Switch company
            </button>
            <button className="dd-i" type="button" onClick={() => { close(); openSettings() }}>
              <BbIcon name="settings" />
              Settings
            </button>
            {can(user.role, 'admin') ? (
              <button className="dd-i" type="button" onClick={() => go('/app/audit-log', close)}>
                <BbIcon name="list" />
                Audit log
              </button>
            ) : null}
          </div>

          <div className="dd-sep" />
          <div className="bb-menu-list">
            <button
              className="dd-i bb-menu-danger"
              type="button"
              onClick={() => {
                close()
                void logout().then(() => navigate('/login', { replace: true }))
              }}
            >
              <BbIcon name="logout" />
              Sign out
            </button>
          </div>
        </>
      )}
    </Dropdown>
  )
}

export function TopBar() {
  return (
    <header className="bb bb-appbar">
      <div className="bb-appbar-left">
        <Link className="bb-brand" to="/app">
          <BbIcon name="bank" />
          BatchBooks
        </Link>
        <span className="bb-appbar-sep" />
        <CompanySwitcher />
      </div>
      <div className="bb-appbar-right">
        <CreditsPill />
        <span className="bb-user-group">
          <UserMenu />
        </span>
      </div>
    </header>
  )
}
